import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import { PublicCrewInfo } from '../../hooks/usePublicCrewInfo';
import JoinWithInviteCode from '../home/JoinWithInviteCode';
import axiosService from '../../services/axios.service';

// Stable accent color derived from the crew name
const getAccentColor = (name: string): string => {
    const palette = ['#5865F2', '#3ba55d', '#faa81a', '#9c84ec', '#e91e8c', '#1e88e5'];
    const hash = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return palette[hash % palette.length];
};

interface CrewPreviewProps {
    crew: PublicCrewInfo;
    error?: string;
}

export const CrewPreview: React.FC<CrewPreviewProps> = ({ crew, error }) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [joinLoading, setJoinLoading] = useState(false);
    const [joinError, setJoinError] = useState<string | null>(null);

    const accent = getAccentColor(crew.name);
    const isArchived = crew.status === 'archived';
    const isSuspended = crew.status === 'suspended';
    const canJoin = crew.autoJoin && !isArchived && !isSuspended;
    const needsInvite = !crew.autoJoin && !isArchived && !isSuspended;

    const handleJoin = async () => {
        setJoinLoading(true);
        setJoinError(null);
        try {
            await axiosService.post(`/c/${crew.slug}/join`);
            await queryClient.invalidateQueries({ queryKey: ['crews', 'detail', crew.slug] });
            await queryClient.invalidateQueries({ queryKey: ['crewMember', crew.slug] });
            navigate(`/c/${crew.slug}`);
        } catch (err: any) {
            setJoinLoading(false);
            setJoinError(err?.response?.data?.error ?? 'Failed to join crew. Please try again.');
        }
    };

    return (
        <Box
            sx={{
                minHeight: 'calc(100vh - 192px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
                px: 2,
            }}
        >
            <Card
                elevation={4}
                sx={{
                    width: '100%',
                    maxWidth: 460,
                    borderRadius: 3,
                    overflow: 'hidden',
                }}
            >
                {/* Banner */}
                <Box
                    sx={{
                        height: 160,
                        background: crew.headerImage
                            ? `url(${crew.headerImage}) center / cover`
                            : `linear-gradient(135deg, ${accent}bb 0%, ${accent} 100%)`,
                    }}
                />

                {/* Body */}
                <Box sx={{ px: 3, pb: 3 }}>
                    {/* Avatar overlapping the banner */}
                    <Avatar
                        sx={{
                            width: 72,
                            height: 72,
                            fontSize: 28,
                            fontWeight: 700,
                            bgcolor: accent,
                            border: '4px solid',
                            borderColor: 'background.paper',
                            mt: '-36px',
                            mb: 1.5,
                        }}
                    >
                        {crew.name[0].toUpperCase()}
                    </Avatar>

                    {/* Name + member count */}
                    <Stack spacing={0.25} sx={{ mb: 2 }}>
                        <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
                            {crew.name}
                        </Typography>
                        {crew.memberCount !== undefined && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <PeopleOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                <Typography variant="caption" color="text.secondary">
                                    {crew.memberCount.toLocaleString()}{' '}
                                    {crew.memberCount === 1 ? 'member' : 'members'}
                                </Typography>
                            </Stack>
                        )}
                    </Stack>

                    {/* Description */}
                    {crew.description && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                mb: 2,
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {crew.description}
                        </Typography>
                    )}

                    <Divider sx={{ mb: 2 }} />

                    {/* Status alerts */}
                    {isArchived && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            This crew has been archived and is no longer accepting new members.
                        </Alert>
                    )}
                    {isSuspended && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            This crew is currently suspended. Contact the crew administrator.
                        </Alert>
                    )}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {/* Actions */}
                    {canJoin && (
                        <Stack spacing={1.5}>
                            {joinError && <Alert severity="error">{joinError}</Alert>}
                            <Button
                                variant="contained"
                                size="large"
                                fullWidth
                                onClick={handleJoin}
                                disabled={joinLoading}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    bgcolor: accent,
                                    '&:hover': { bgcolor: accent, filter: 'brightness(0.9)' },
                                }}
                            >
                                {joinLoading ? 'Joining…' : `Join ${crew.name}`}
                            </Button>
                        </Stack>
                    )}

                    {needsInvite && (
                        <Stack spacing={1.5}>
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                This crew requires an invite code to join.
                            </Typography>
                            <JoinWithInviteCode onSuccess={(slug) => navigate(`/c/${slug}`)} />
                        </Stack>
                    )}
                </Box>

                <Divider />
                <Box sx={{ px: 3, py: 1.5, textAlign: 'center' }}>
                    <Button
                        size="small"
                        onClick={() => navigate('/join')}
                        sx={{ color: 'text.secondary', textTransform: 'none' }}
                    >
                        Browse other crews
                    </Button>
                </Box>
            </Card>
        </Box>
    );
};
